// Гайд: как устроено приложение, первые шаги, обычный день, разделы со ссылками и словарь.

import { useEffect, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Card, Icon } from '@/components/ui'

import { DAY, FIRST_STEPS, IDEAS, SECTIONS, TERMS, type GuideLink, type GuideStep } from './content'

const CONTENTS: { id: string; label: string }[] = [
  { id: 'first-steps', label: 'Первые шаги' },
  { id: 'day', label: 'Обычный день' },
  ...SECTIONS.map((s) => ({ id: s.id, label: s.title })),
  { id: 'terms', label: 'Словарь' },
]

const chip =
  'inline-flex min-h-11 items-center rounded-lg border border-border bg-surface px-3 text-sm font-medium text-fg hover:bg-surface-2 lg:min-h-8'

const go =
  'inline-flex min-h-11 items-center gap-1 rounded-lg bg-accent-soft px-3 text-sm font-medium text-accent hover:opacity-90 lg:min-h-8'

function scrollToId(id: string) {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  document.getElementById(id)?.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
}

/** Ссылка внутри гайда: прокрутка к блоку, адрес /guide#id можно отправить или открыть заново. */
function Anchor({ id, className, children }: { id: string; className?: string; children: ReactNode }) {
  const jump = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    scrollToId(id)
    window.history.replaceState(window.history.state, '', `#${id}`)
  }
  return (
    <a href={`#${id}`} onClick={jump} className={className}>
      {children}
    </a>
  )
}

function OpenLink({ link }: { link: GuideLink }) {
  return (
    <Link to={link.to} className={go}>
      {link.label}
      <Icon name="chevron-right" size={16} />
    </Link>
  )
}

function ToContents() {
  return (
    <Anchor id="contents" className="inline-flex min-h-11 items-center text-xs text-muted hover:text-fg lg:min-h-8">
      К содержанию
    </Anchor>
  )
}

function Steps({ steps, numbered }: { steps: GuideStep[]; numbered?: boolean }) {
  const List = numbered ? 'ol' : 'ul'
  return (
    <List className="flex flex-col divide-y divide-border">
      {steps.map((step, i) => (
        <li key={step.title} className="flex gap-3 py-3 first:pt-0 last:pb-0">
          {numbered ? (
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent"
            >
              {i + 1}
            </span>
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">{step.title}</p>
              <p className="mt-0.5 text-sm text-muted">{step.text}</p>
            </div>
            <div className="shrink-0">
              <OpenLink link={step.link} />
            </div>
          </div>
        </li>
      ))}
    </List>
  )
}

export function GuidePage() {
  const { hash } = useLocation()

  // Открыли /guide#plan — сразу к нужному блоку.
  useEffect(() => {
    if (hash) scrollToId(decodeURIComponent(hash.slice(1)))
  }, [hash])

  const block = 'scroll-mt-4'
  return (
    <>
      <PageHeader title="Гайд" description="Как устроено приложение, с чего начать и где что лежит." />
      <div className="flex flex-col gap-4">
        <Card title="Коротко о приложении">
          <p className="text-sm text-fg">
            Здесь в одном месте вес, здоровье, тренировки и данные часов Garmin. Приложение строит план тренировок,
            подсказывает, как тренироваться сегодня, и помнит каждое изменение.
          </p>
          <ul className="mt-4 grid gap-3 md:grid-cols-3">
            {IDEAS.map((idea) => (
              <li key={idea.title} className="flex gap-3 rounded-lg bg-surface-2 p-3">
                <Icon name={idea.icon} size={20} className="mt-0.5 shrink-0 text-accent" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-fg">{idea.title}</p>
                  <p className="mt-0.5 text-sm text-muted">{idea.text}</p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted">
            Разделы на компьютере — в меню слева, на телефоне — в панели внизу: «Сегодня», «Тренировка», «План»,
            «Здоровье». Остальное, и этот гайд тоже, — в «Ещё». Если только начинаешь, пройди{' '}
            <Anchor id="first-steps" className="font-medium text-accent">
              первые шаги
            </Anchor>{' '}
            по порядку.
          </p>
        </Card>

        <Card id="contents" title="Содержание" className={block}>
          <nav aria-label="Содержание гайда">
            <ul className="flex flex-wrap gap-2">
              {CONTENTS.map((item) => (
                <li key={item.id}>
                  <Anchor id={item.id} className={chip}>
                    {item.label}
                  </Anchor>
                </li>
              ))}
            </ul>
          </nav>
        </Card>

        <Card
          id="first-steps"
          title="Первые шаги"
          subtitle="По порядку — так приложение быстрее станет полезным. Часть из них видна на «Сегодня» в карточке «Начни отсюда»."
          actions={<ToContents />}
          className={block}
        >
          <Steps steps={FIRST_STEPS} numbered />
        </Card>

        <Card
          id="day"
          title="Обычный день"
          subtitle="Как приложение вписывается в день, когда всё настроено."
          actions={<ToContents />}
          className={block}
        >
          <Steps steps={DAY} />
        </Card>

        <h2 className="mt-2 text-base font-semibold text-fg">Разделы</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {SECTIONS.map((section) => (
            <Card
              key={section.id}
              id={section.id}
              title={
                <span className="flex items-center gap-2">
                  <Icon name={section.icon} size={18} className="text-accent" />
                  {section.title}
                </span>
              }
              subtitle={section.summary}
              actions={<ToContents />}
              className={block}
            >
              <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-fg marker:text-muted">
                {section.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2">
                {section.links.map((link) => (
                  <OpenLink key={link.to} link={link} />
                ))}
              </div>
            </Card>
          ))}
        </div>

        <Card id="terms" title="Словарь" subtitle="Слова, которые встречаются на экранах." actions={<ToContents />} className={block}>
          <dl className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            {TERMS.map((t) => (
              <div key={t.term} className="text-sm">
                <dt className="inline font-semibold text-fg">{t.term}</dt>
                <dd className="inline text-muted"> — {t.text}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <p className="min-w-0 flex-1 text-sm text-fg">
              Остался вопрос о своих данных? Спроси ИИ — он видит сон, вес, тренировки и план.
            </p>
            <OpenLink link={{ to: '/ai/chat', label: 'Спросить в чате' }} />
          </div>
        </Card>
      </div>
    </>
  )
}
